// Utilities
import {
  getRequest,
  getTable,
  postRequest,
  getDownloadExcel,
  deleteRequest,
} from "./requestTemplates";
// Types
import { Report } from "src/Interfaces/TableInterfaces";
import type {
  IUnderlyingAssetsSearchParams,
  IUnderlyingAssetsTableReport,
  IPairPositionSearchParams,
  IPairPositionTableReport,
  IAssumptionsParams,
  ISetAssumptionsParams,
  IActiveAssets,
  IVSetting,
  ICalculatorParams,
} from "src/Interfaces/iMEMarketWatch";
import { IStepOptionDropdownItem } from "../Interfaces/stepInterfaces";
import { Packet } from "../Interfaces/commonDtos";

/**
 * Market Watch => IME => underlying assets
 * جدول دارایی پایه
 *
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getBaseAssetTable = (
  searchParam: {},
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable("/v1/IME/GetBaseAssets", searchParam, offset, pageSize, sortingType);

/**
 * Market Watch => IME => underlying assets
 * جدول مشخصات دارایی پایه
 *
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getBaseSpecTable = (
  searchParam: IUnderlyingAssetsSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IUnderlyingAssetsTableReport>> =>
  getTable<IUnderlyingAssetsSearchParams, IUnderlyingAssetsTableReport>(
    "/v1/IME/GetBaseInstruments",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * Market Watch => IME => underlying assets
 * نماد
 *
 * @async
 * @version 1.4.0
 */
export const getBaseInstruments = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetBaseAssetInstrumentDropdown");

/**
 * Market Watch => IME => underlying assets
 * order book
 *
 * @param {string} insCode
 * @async
 * @version 1.4.0
 */
export const getOrderbook = (insCode: string): Promise<any> =>
  postRequest("/v1/IME/GetBaseAssetOrderbook", { insCode });

/**
 * Market Watch => IME =>
 * دانلود فایل اکسل
 *
 * @async
 * @version 1.4.0
 */
export const getBaseAssetExcel = () => {
  const rand = Math.random();

  return getDownloadExcel(`/v1/IME/BaseAssetExcel?${rand}`);
};

/**
 * Market Watch => IME => underlying assets
 * کلاستر
 *
 * @async
 * @version 1.4.0
 */
export const getBaseCluster = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetBaseClusterDropdown");

/**
 * Market Watch => IME => underlying assets
 * گرفتن لیست نمادهای پایه براساس کلاستر
 *
 * @param {string} clusterId
 * @async
 * @version 1.4.0
 */
export const getBaseAssetByCluster = (
  clusterId: string,
): Promise<IStepOptionDropdownItem[]> =>
  getRequest(`/v1/IME/GetBaseAssetsByClusterIdDropdown?clusterId=${clusterId}`);

/**
 * Market Watch => IME => underlying assets
 * افزودن نماد پایه
 *
 * @param {string} ids
 * @async
 * @version 1.4.0
 */
export const addActiveBaseAssets = (ids: string[]): Promise<any> =>
  postRequest("/v1/IME/AddActiveBaseAssets", { ids });

/**
 * Market Watch => IME => underlying assets
 * لیست نمادهای پایه فعال
 *
 * @async
 * @version 1.4.0
 */
export const getActiveClusterAndBaseAssets = (): Promise<IActiveAssets[]> =>
  getRequest("/v1/IME/GetActiveBaseAssets");

/**
 * Market Watch => IME => underlying assets
 * حذف نماد پایه
 *
 * @param {string} id
 * @async
 * @version 1.4.0
 */
export const deleteActiveCluster = (id: string): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/IME/RemoveActiveBaseAsset?id=${id}`);

//------------------------------Futures------------------------------

/**
 * Market Watch => IME => Futures
 * جدول آتی
 *
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getFuturesTable = (
  searchParam: {},
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable("/v1/IME/GetFutures", searchParam, offset, pageSize, sortingType);

/**
 * Market Watch => IME => Futures
 * جدول مشخصات قراردادهای آتی
 *
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getFutureSpecTable = (
  searchParam: IUnderlyingAssetsSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IUnderlyingAssetsTableReport>> =>
  getTable<IUnderlyingAssetsSearchParams, IUnderlyingAssetsTableReport>(
    "/v1/IME/GetFutureInstruments",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * Market Watch => IME => Futures
 * نماد
 *
 * @async
 * @version 1.4.0
 */
export const getFutureInstruments = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetFutureInstrumentDropdown");

/**
 * Market Watch => IME => Futures
 * order book
 *
 * @param {string} insCode
 * @async
 * @version 1.4.0
 */
export const getFutureOrderbook = (insCode: string): Promise<any> =>
  postRequest("/v1/IME/GetFutureOrderbook", { insCode });

/**
 * Market Watch => IME => Futures
 * دانلود فایل اکسل
 *
 * @async
 * @version 1.4.0
 */
export const getFutureExcel = () => {
  const rand = Math.random();

  return getDownloadExcel(`/v1/IME/FutureExcel?${rand}`);
};
//------------------------------Options------------------------------

/**
 * Market Watch => IME => Options
 * جدول اختیار معامله
 *
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getOptionsTable = (
  searchParam: {},
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable("/v1/IME/GetOptions", searchParam, offset, pageSize, sortingType);

/**
 * Market Watch => IME => Options
 * جدول مشخصات قراردادهای آتی
 *
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getOptionsSpecTable = (
  searchParam: IUnderlyingAssetsSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IUnderlyingAssetsTableReport>> =>
  getTable<IUnderlyingAssetsSearchParams, IUnderlyingAssetsTableReport>(
    "/v1/IME/GetOptionInstruments",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * Market Watch => IME => Options
 * نماد
 *
 * @async
 * @version 1.4.0
 */
export const getOptionsInstruments = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetOptionInstrumentDropdown");

/**
 * Market Watch => IME => Options
 * order book
 *
 * @param {string} contractCode
 * @async
 * @version 1.4.0
 */
export const getOptionsOrderbook = (insCode: string): Promise<any> =>
  postRequest("/v1/IME/GetOptionOrderbook", { insCode });

/**
 * Market Watch => IME => Options
 * دانلود فایل اکسل
 *
 * @async
 * @version 1.4.0
 */
export const getOptionExcel = () => {
  const rand = Math.random();

  return getDownloadExcel(`/v1/IME/OptionExcel?${rand}`);
};

export const getGreekSymbolConstraint = (): Promise<any> =>
  getRequest("/v1/IME/GetGreekSymbolsConfig");

/**
 * Market Watch => IME => Options
 * اعمال نرخ بهره
 *
 * @param {number} riskfreeRate
 * @async
 * @version 1.4.0
 */
export const setServerRiskFreeRate = (
  riskfreeRate: number,
): Promise<Packet<boolean>> =>
  postRequest("/v1/IME/ChangeRiskFreeRate", { riskfreeRate });

/**
 * Market Watch => IME => Options
 * ثبت فرم iv
 *
 * @param {number} riskfreeRate
 * @async
 * @version 1.4.0
 */
export const upsertGreekSymbolConstraint = (
  formValues: IVSetting,
): Promise<Packet<boolean>> =>
  postRequest("/v1/IME/AddOrUpdateGreekSymbolConstraint", formValues);

//------------------------------Pair Position------------------------------

/**
 * Market Watch => IME => Pair Position
 * دانلود فایل اکسل جفت پوزیشن
 *
 * @param {IPairPositionSearchParams} data
 * @async
 * @version 1.4.0
 */
export const getPaiPositionExcel = (searchParam: IPairPositionSearchParams) => {
  const rand = Math.random();

  return getDownloadExcel(`/v1/IME/NewPairPositionExcel?${rand}`);
};

/**
 * Market Watch => IME => Pair Position
 * جدول جفت پوزیشن
 *
 * @param {IPairPositionSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPairPositionTable = (
  searchParam: IPairPositionSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPairPositionTableReport>> =>
  getTable<IPairPositionSearchParams, IPairPositionTableReport>(
    "/v1/IME/NewPairPosition",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * Market Watch => IME => Pair Position
 * نماد
 *
 * @async
 * @version 1.4.0
 */
export const getBourseKalaInstrument = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetNewBaseInstrumentDropdown");

/**
 * Market Watch => IME => Pair Position
 * گروه
 *
 * @async
 * @version 1.4.0
 */
export const getGroupInstrument = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetBaseGroupInstrumentDropdown");

/**
 * Market Watch => IME => Pair Position
 * نوع جفت
 *
 * @async
 * @version 1.4.0
 */
export const getPairType = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetPairTypeInstrumentDropdown");
/**
 * Market Watch => IME => Pair Position
 * "نماد سمت اول,
 *
 * @async
 * @version 1.4.0
 */
export const getFirstSide = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetFirstPairCodeDropdown");
/**
 * Market Watch => IME => Pair Position
 * "نماد سمت دوم",
 *
 * @async
 * @version 1.4.0
 */
export const getSecondSide = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetSecondPairCodeDropdown");
/**
 * Market Watch => IME => Pair Position
 *lمقایسه ماه تا سررسید
 *
 * @async
 * @version 1.4.0
 */
export const getDueDateMonths = (): Promise<IStepOptionDropdownItem[]> =>
  postRequest(`/v1/IME/GetLastTradingMonthTypeDropdown`, {});

/**
 *  Market Watch => IME => Pair Position
 *  order book
 *
 * @param {firstContractCode} string
 * @param {secondContractCode} string
 * @async
 * @version 1.4.0
 */

export const getPairPositionOrderbook = (
  firstContractCode: string,
  secondContractCode: string,
): Promise<any> =>
  postRequest("/v1/IME/GetNewPairPositionOrderbook", {
    firstContractCode,
    secondContractCode,
  });

/**
 * Market Watch => IME => Pair Position
 *  مفروضات
 *
 * @async
 * @version 1.4.0
 */
export const getAssumptionsConfig = (): Promise<IAssumptionsParams> =>
  getRequest("/v1/IME/GetAssumptionsConfig");

/**
 * Market Watch => IME => Pair Position
 * ثیت مفروضات
 *
 * @param {IFilterParams} data
 * @async
 * @version 1.4.0
 */
export const setAssumptionsConfig = (
  config: ISetAssumptionsParams,
): Promise<any> => postRequest("/v1/IME/SetAssumptionsConfig", config);

/**
 * Market Watch => IME => Pair Position
 *  ماشین حساب => انتخاب حالت
 *
 * @async
 * @version 1.4.0
 */
export const getFirst = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetCoveredCallCalculationTypesDropDown");

/**
 * Market Watch => IME => Pair Position
 *  ماشین حساب => نماد کال
 *
 * @async
 * @version 1.4.0
 */
export const getCallOption = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/IME/GetCallOptionsDropDown");

/**
 * Market Watch => IME => Pair Position
 *  گرفتن مقادیر براساس نماد کال
 *
 * @param {string} contractCode
 * @async
 * @version 1.4.0
 */
export const getCallOptionData = (
  contractCode: string,
): Promise<Packet<ICalculatorParams>> =>
  getRequest(`/v1/IME/GetCallOption?contractCode=${contractCode}`);

/**
 * Market Watch => IME => Pair Position
 * ارسال و گرفتن مقادیر برای حالت اول
 *
 * @param {ICalculatorParams} formValues
 * @async
 * @version 1.4.0
 */
export const sentType1 = (
  formValues: ICalculatorParams,
): Promise<Packet<ICalculatorParams>> =>
  postRequest("/v1/IME/GetReturnCalculation", formValues);

/**
 * Market Watch => IME => Pair Position
 * ارسال و گرفتن مقادیر برای حالت دوم
 *
 * @param {ICalculatorParams} formValues
 * @async
 * @version 1.4.0
 */
export const sentType2 = (
  formValues: ICalculatorParams,
): Promise<Packet<ICalculatorParams>> =>
  postRequest("/v1/IME/GetCallPriceCalculation", formValues);

/**
 * Market Watch => IME => Pair Position
 * ارسال و گرفتن مقادیر برای حالت سوم
 *
 * @param {ICalculatorParams} formValues
 * @async
 * @version 1.4.0
 */
export const sentType3 = (
  formValues: ICalculatorParams,
): Promise<Packet<ICalculatorParams>> =>
  postRequest("/v1/IME/GetUnderlyingAssetPriceCalculation", formValues);

/**
 * Market Watch => IME => Pair Position
 *  محاسبه باده دوره
 *
 * @param {number} dtm
 * @param {number} annualCompoundedProfitPercent
 * @async
 * @version 1.4.0
 */
export const getHPR = (
  dtm: number,
  annualCompoundedProfitPercent: number,
): Promise<Packet<string>> =>
  getRequest(
    `/v1/IME/GetHPR?annualCompoundedProfitPercent=${annualCompoundedProfitPercent}&dtm=${dtm}`,
  );

/**
 * Market Watch => IME => Pair Position
 *  ماشین حساب => دکمه ثبت
 *
 * @param {ICalculatorParams} formValues
 * @async
 * @version 1.4.0
 */
export const submit = (
  formValues: ICalculatorParams,
): Promise<Packet<ICalculatorParams>> =>
  postRequest("/v1/IME/GetAllCoveredCallCalculation", formValues);
